/**
 * WorkspaceSimulation - Profession-specific immersive workspace environments.
 *
 * Renders the workspace task generated by the backend as a realistic tool UI:
 * - software_ide:    VS Code-like editor (file tree, tabs, line numbers, console)
 * - spreadsheet:     Excel-like grid (editable cells, change tracking)
 * - email_inbox:     Outlook-like inbox (list, reading pane, replies, priorities)
 * - data_notebook:   Jupyter-like notebook (code/markdown cells with outputs)
 * - scada_panel:     Industrial SCADA panel (process units, readings, alarms)
 * - design_review:   CAD-like engineering design review (parts, specs)
 * - crm_pipeline:    CRM deal pipeline (kanban columns by stage)
 * - legal_review:    Contract review (clauses, risk flagging, redline notes)
 *
 * The candidate works inside the environment and submits once; the submission
 * shape depends on the workspace type and is evaluated by the backend AI.
 */

import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useWorkspaceInteractions } from './WorkspaceInteractions';
import {
  FileCode,
  Terminal,
  Send,
  Mail,
  MailOpen,
  Reply,
  AlertTriangle,
  AlertCircle,
  Info,
  Gauge,
  Table,
  BookOpen,
  DraftingCompass,
  Scale,
  TrendingUp,
  Flag,
  CheckCircle2,
  Cpu,
  Wrench,
  Zap,
  Database,
  Network,
  Package,
} from 'lucide-react';

// ============================================================================
// Types
// ============================================================================

export interface WorkspaceTask {
  workspace_type: string;
  task_title?: string;
  task_description?: string;
  [key: string]: any;
}

interface WorkspaceSimulationProps {
  task: WorkspaceTask;
  submitting?: boolean;
  onSubmit: (submission: Record<string, any>) => void;
  /** Extra interactive sub-task blocks (drag-drop, wiring...) rendered inside the scroll area */
  extras?: React.ReactNode;
}

// ============================================================================
// Shared pieces
// ============================================================================

const TaskHeader: React.FC<{ icon: React.ReactNode; title?: string; description?: string; accent: string }> = ({
  icon,
  title,
  description,
  accent,
}) => (
  <div className={`rounded-lg border p-4 ${accent}`}>
    <div className="flex items-start gap-3">
      <div className="flex-shrink-0 mt-0.5">{icon}</div>
      <div className="min-w-0">
        {title && <h3 className="font-semibold text-gray-900 text-sm md:text-base">{title}</h3>}
        {description && <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">{description}</p>}
      </div>
    </div>
  </div>
);

const QuestionAnswers: React.FC<{
  questions: string[];
  answers: string[];
  onChange: (index: number, value: string) => void;
  disabled?: boolean;
}> = ({ questions, answers, onChange, disabled }) => {
  const { t } = useTranslation();
  return (
    <div className="space-y-4">
      {questions.map((q, i) => (
        <div key={i} className="space-y-1.5">
          <label className="text-sm font-medium text-gray-800 flex gap-2">
            <span className="flex-shrink-0 w-5 h-5 rounded-full bg-gray-800 text-white text-[11px] flex items-center justify-center">
              {i + 1}
            </span>
            {q}
          </label>
          <textarea
            value={answers[i] || ''}
            onChange={(e) => onChange(i, e.target.value)}
            rows={3}
            className="input text-sm w-full"
            placeholder={t('workspace.answerPlaceholder') as string}
            disabled={disabled}
          />
        </div>
      ))}
    </div>
  );
};

const SubmitBar: React.FC<{
  onSubmit: () => void;
  submitting?: boolean;
  disabled?: boolean;
  hint?: string;
}> = ({ onSubmit, submitting, disabled, hint }) => {
  const { t } = useTranslation();
  return (
    <div className="bg-white border-t border-gray-200 p-4 sticky bottom-0 z-20">
      <div className="max-w-6xl mx-auto flex items-center justify-between gap-4">
        <p className="text-xs text-gray-500 flex-1">{hint || ''}</p>
        <button
          onClick={onSubmit}
          disabled={submitting || disabled}
          className="btn-primary flex items-center gap-2 disabled:opacity-50"
        >
          <Send size={16} />
          {submitting
            ? t('workspace.submitting')
            : t('workspace.submit')}
        </button>
      </div>
    </div>
  );
};

// ============================================================================
// 1) Software IDE (VS Code look)
// ============================================================================

const IdeWorkspace: React.FC<WorkspaceSimulationProps> = ({ task, submitting, onSubmit, extras }) => {
  const { t } = useTranslation();
  const initialFiles = useMemo(
    () =>
      (Array.isArray(task.files) ? task.files : []).map((f: any) => ({
        name: String(f?.name || 'file.txt'),
        language: String(f?.language || 'plaintext'),
        content: String(f?.content || ''),
        readonly: f?.readonly === true,
      })),
    [task]
  );
  const [fileContents, setFileContents] = useState<string[]>(initialFiles.map((f) => f.content));
  const [activeFile, setActiveFile] = useState(0);
  const [explanation, setExplanation] = useState('');
  const gutterRef = useRef<HTMLDivElement | null>(null);

  const current = initialFiles[activeFile];
  const currentContent = fileContents[activeFile] ?? '';
  const lineCount = Math.max(currentContent.split('\n').length, 1);

  const handleSubmit = () => {
    onSubmit({
      files: initialFiles.map((f, i) => ({ name: f.name, content: fileContents[i] ?? f.content })),
      explanation,
    });
  };

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="flex-1 overflow-y-auto p-4">
        <div className="max-w-6xl mx-auto space-y-4">
          <TaskHeader
            icon={<FileCode size={20} className="text-indigo-600" />}
            title={task.task_title}
            description={task.task_description}
            accent="bg-indigo-50 border-indigo-200"
          />

          {/* IDE frame */}
          <div className="rounded-lg overflow-hidden border border-gray-800 shadow-lg bg-[#1e1e1e] text-gray-200">
            {/* Title bar */}
            <div className="flex items-center gap-2 px-3 py-2 bg-[#323233] text-xs text-gray-400 select-none">
              <span className="w-3 h-3 rounded-full bg-red-500/80" />
              <span className="w-3 h-3 rounded-full bg-yellow-500/80" />
              <span className="w-3 h-3 rounded-full bg-green-500/80" />
              <span className="ml-3 truncate">{task.task_title || 'workspace'} — {t('workspace.codeEditor')}</span>
            </div>

            <div className="flex" style={{ minHeight: 380 }}>
              {/* File tree */}
              <div className="w-44 flex-shrink-0 bg-[#252526] border-r border-[#3c3c3c] py-2 hidden sm:block">
                <div className="px-3 pb-2 text-[10px] uppercase tracking-wider text-gray-500">
                  {t('workspace.explorer')}
                </div>
                {initialFiles.map((f, i) => (
                  <button
                    key={f.name + i}
                    onClick={() => setActiveFile(i)}
                    className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 ${
                      i === activeFile ? 'bg-[#37373d] text-white' : 'text-gray-400 hover:bg-[#2a2d2e]'
                    }`}
                  >
                    <FileCode size={12} className="flex-shrink-0 text-blue-400" />
                    <span className="truncate">{f.name}</span>
                    {f.readonly && <span className="ml-auto text-[9px] text-gray-500">{t('workspace.readonly')}</span>}
                  </button>
                ))}
              </div>

              {/* Editor */}
              <div className="flex-1 flex flex-col min-w-0">
                {/* Tabs */}
                <div className="flex bg-[#252526] border-b border-[#3c3c3c] overflow-x-auto">
                  {initialFiles.map((f, i) => (
                    <button
                      key={f.name + i}
                      onClick={() => setActiveFile(i)}
                      className={`px-4 py-2 text-xs whitespace-nowrap border-r border-[#3c3c3c] ${
                        i === activeFile
                          ? 'bg-[#1e1e1e] text-white border-t-2 border-t-indigo-400'
                          : 'text-gray-400 hover:bg-[#2d2d2d]'
                      }`}
                    >
                      {f.name}
                    </button>
                  ))}
                </div>

                {/* Code area with line numbers */}
                <div className="flex-1 flex relative min-h-[260px]">
                  <div
                    ref={gutterRef}
                    className="w-11 flex-shrink-0 bg-[#1e1e1e] text-right pr-2 pt-3 text-xs leading-5 text-gray-600 font-mono select-none overflow-hidden"
                  >
                    {Array.from({ length: lineCount }, (_, i) => (
                      <div key={i}>{i + 1}</div>
                    ))}
                  </div>
                  <textarea
                    value={currentContent}
                    onChange={(e) => {
                      if (current?.readonly) return;
                      setFileContents((prev) => {
                        const next = [...prev];
                        next[activeFile] = e.target.value;
                        return next;
                      });
                    }}
                    onScroll={(e) => {
                      if (gutterRef.current) gutterRef.current.scrollTop = (e.target as HTMLTextAreaElement).scrollTop;
                    }}
                    spellCheck={false}
                    readOnly={current?.readonly}
                    className="flex-1 bg-[#1e1e1e] text-[#d4d4d4] font-mono text-xs leading-5 p-3 pl-1 outline-none resize-none min-h-[260px] whitespace-pre"
                    style={{ tabSize: 4 }}
                  />
                </div>

                {/* Console */}
                {task.console_output && (
                  <div className="border-t border-[#3c3c3c] bg-[#181818]">
                    <div className="flex items-center gap-2 px-3 py-1.5 text-[10px] uppercase tracking-wider text-gray-500 border-b border-[#3c3c3c]">
                      <Terminal size={11} />
                      {t('workspace.console')}
                    </div>
                    <pre className="p-3 text-xs font-mono text-red-300/90 whitespace-pre-wrap max-h-40 overflow-y-auto">
                      {task.console_output}
                    </pre>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Explanation */}
          <div className="card space-y-2">
            <label className="text-sm font-medium text-gray-800">
              {t('workspace.ideExplanation')}
            </label>
            <textarea
              value={explanation}
              onChange={(e) => setExplanation(e.target.value)}
              rows={3}
              className="input text-sm w-full"
              placeholder={t('workspace.ideExplanationPlaceholder') as string}
            />
          </div>
          {extras}
        </div>
      </div>
      <SubmitBar
        onSubmit={handleSubmit}
        submitting={submitting}
        hint={t('workspace.ideHint') as string}
      />
    </div>
  );
};

// ============================================================================
// 2) Spreadsheet (Excel look)
// ============================================================================

const colLetter = (i: number) => String.fromCharCode(65 + i);

const SpreadsheetWorkspace: React.FC<WorkspaceSimulationProps> = ({ task, submitting, onSubmit, extras }) => {
  const { t } = useTranslation();
  const columns: string[] = Array.isArray(task.sheet?.columns) ? task.sheet.columns : [];
  const originalRows: string[][] = useMemo(
    () => (Array.isArray(task.sheet?.rows) ? task.sheet.rows.map((r: any) => (Array.isArray(r) ? r.map(String) : [])) : []),
    [task]
  );
  const [rows, setRows] = useState<string[][]>(originalRows.map((r) => [...r]));
  const [selected, setSelected] = useState<{ r: number; c: number } | null>(null);
  const [answerText, setAnswerText] = useState('');

  const changedCells = useMemo(() => {
    const changes: { row: number; col: number; column_name: string; old: string; new: string }[] = [];
    rows.forEach((row, r) => {
      row.forEach((val, c) => {
        const oldVal = originalRows[r]?.[c] ?? '';
        if (val !== oldVal) {
          changes.push({ row: r + 1, col: c + 1, column_name: columns[c] || colLetter(c), old: oldVal, new: val });
        }
      });
    });
    return changes;
  }, [rows, originalRows, columns]);

  const handleSubmit = () => {
    onSubmit({ changed_cells: changedCells, answer_text: answerText });
  };

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="flex-1 overflow-y-auto p-4">
        <div className="max-w-6xl mx-auto space-y-4">
          <TaskHeader
            icon={<Table size={20} className="text-emerald-600" />}
            title={task.task_title}
            description={task.task_description}
            accent="bg-emerald-50 border-emerald-200"
          />

          {/* Spreadsheet frame */}
          <div className="rounded-lg overflow-hidden border border-gray-300 shadow bg-white">
            {/* Toolbar */}
            <div className="flex items-center gap-3 px-3 py-2 bg-[#217346] text-white text-xs">
              <Table size={14} />
              <span className="font-medium truncate">{task.task_title || 'Sheet1'} — Tablo</span>
            </div>
            {/* Formula bar */}
            <div className="flex items-center gap-2 px-2 py-1.5 border-b border-gray-300 bg-gray-50 text-xs">
              <span className="w-14 text-center font-mono bg-white border border-gray-300 rounded px-1 py-0.5">
                {selected ? `${colLetter(selected.c)}${selected.r + 1}` : ''}
              </span>
              <span className="text-gray-400">fx</span>
              <span className="flex-1 font-mono text-gray-700 truncate bg-white border border-gray-300 rounded px-2 py-0.5">
                {selected ? rows[selected.r]?.[selected.c] ?? '' : ''}
              </span>
            </div>
            {/* Grid */}
            <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
              <table className="border-collapse w-full text-xs">
                <thead className="sticky top-0 z-10">
                  <tr>
                    <th className="bg-gray-100 border border-gray-300 w-9 sticky left-0" />
                    {columns.map((col, c) => (
                      <th key={c} className="bg-gray-100 border border-gray-300 px-2 py-1.5 font-semibold text-gray-700 min-w-[110px]">
                        <div className="text-[9px] text-gray-400 font-normal">{colLetter(c)}</div>
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, r) => (
                    <tr key={r}>
                      <td className="bg-gray-100 border border-gray-300 text-center text-gray-500 font-medium sticky left-0">
                        {r + 1}
                      </td>
                      {columns.map((_, c) => {
                        const isChanged = (originalRows[r]?.[c] ?? '') !== (row[c] ?? '');
                        const isSel = selected?.r === r && selected?.c === c;
                        return (
                          <td
                            key={c}
                            className={`border p-0 ${
                              isSel ? 'border-emerald-600 border-2' : 'border-gray-200'
                            } ${isChanged ? 'bg-amber-50' : 'bg-white'}`}
                          >
                            <input
                              value={row[c] ?? ''}
                              onFocus={() => setSelected({ r, c })}
                              onChange={(e) => {
                                const val = e.target.value;
                                setRows((prev) => {
                                  const next = prev.map((rr) => [...rr]);
                                  next[r][c] = val;
                                  return next;
                                });
                              }}
                              className={`w-full px-2 py-1 text-xs outline-none bg-transparent ${
                                isChanged ? 'text-amber-800 font-medium' : 'text-gray-800'
                              }`}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {changedCells.length > 0 && (
              <div className="px-3 py-2 bg-amber-50 border-t border-amber-200 text-xs text-amber-800 flex items-center gap-2">
                <CheckCircle2 size={13} />
                {t('workspace.changedCells', { count: changedCells.length })}
              </div>
            )}
          </div>

          {/* Findings */}
          <div className="card space-y-2">
            <label className="text-sm font-medium text-gray-800">
              {t('workspace.spreadsheetFindings')}
            </label>
            <textarea
              value={answerText}
              onChange={(e) => setAnswerText(e.target.value)}
              rows={4}
              className="input text-sm w-full"
              placeholder={t('workspace.spreadsheetFindingsPlaceholder') as string}
            />
          </div>
          {extras}
        </div>
      </div>
      <SubmitBar
        onSubmit={handleSubmit}
        submitting={submitting}
        disabled={!answerText.trim() && changedCells.length === 0}
        hint={t('workspace.spreadsheetHint') as string}
      />
    </div>
  );
};

// ============================================================================
// 3) Email Inbox (Outlook look)
// ============================================================================

const InboxWorkspace: React.FC<WorkspaceSimulationProps> = ({ task, submitting, onSubmit, extras }) => {
  const { t } = useTranslation();
  const emails: any[] = useMemo(() => (Array.isArray(task.emails) ? task.emails : []), [task]);
  const [activeId, setActiveId] = useState<number | null>(emails[0]?.id ?? null);
  const [readIds, setReadIds] = useState<Set<number>>(new Set(emails[0]?.id != null ? [emails[0].id] : []));
  const [replies, setReplies] = useState<Record<number, string>>({});
  const [priorities, setPriorities] = useState<Record<number, number>>({});
  const [answerText, setAnswerText] = useState('');

  const activeEmail = emails.find((e) => e.id === activeId);

  const handleSubmit = () => {
    const priorityOrder = Object.entries(priorities)
      .filter(([, p]) => p > 0)
      .sort((a, b) => a[1] - b[1])
      .map(([id]) => Number(id));
    onSubmit({
      replies: Object.entries(replies)
        .filter(([, body]) => body.trim())
        .map(([email_id, body]) => ({ email_id: Number(email_id), body: body.trim() })),
      priority_order: priorityOrder,
      answer_text: answerText,
    });
  };

  const repliesCount = Object.values(replies).filter((b) => b.trim()).length;

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="flex-1 overflow-y-auto p-4">
        <div className="max-w-6xl mx-auto space-y-4">
          <TaskHeader
            icon={<Mail size={20} className="text-sky-600" />}
            title={task.task_title}
            description={task.task_description}
            accent="bg-sky-50 border-sky-200"
          />

          {/* Mail client frame */}
          <div className="rounded-lg overflow-hidden border border-gray-300 shadow bg-white">
            <div className="flex items-center gap-2 px-3 py-2 bg-[#0f6cbd] text-white text-xs">
              <Mail size={14} />
              <span className="font-medium">{t('workspace.inbox')}</span>
              <span className="ml-auto opacity-80">{emails.length} {t('workspace.emails')}</span>
            </div>
            <div className="flex flex-col md:flex-row" style={{ minHeight: 380 }}>
              {/* Email list */}
              <div className="md:w-80 flex-shrink-0 border-r border-gray-200 overflow-y-auto max-h-[420px]">
                {emails.map((email) => {
                  const isActive = email.id === activeId;
                  const isRead = readIds.has(email.id);
                  const priority = priorities[email.id] || 0;
                  return (
                    <button
                      key={email.id}
                      onClick={() => {
                        setActiveId(email.id);
                        setReadIds((prev) => new Set(prev).add(email.id));
                      }}
                      className={`w-full text-left px-3 py-2.5 border-b border-gray-100 ${
                        isActive ? 'bg-sky-50 border-l-4 border-l-sky-500' : 'hover:bg-gray-50 border-l-4 border-l-transparent'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        {isRead ? (
                          <MailOpen size={13} className="text-gray-400 flex-shrink-0" />
                        ) : (
                          <Mail size={13} className="text-sky-600 flex-shrink-0" />
                        )}
                        <span className={`text-xs truncate flex-1 ${isRead ? 'text-gray-600' : 'font-semibold text-gray-900'}`}>
                          {email.from_name}
                        </span>
                        <span className="text-[10px] text-gray-400 flex-shrink-0">{email.received}</span>
                      </div>
                      <div className={`text-xs mt-0.5 truncate ${isRead ? 'text-gray-500' : 'font-medium text-gray-800'}`}>
                        {email.subject}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        {priority > 0 && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-medium">
                            {t('workspace.priorityShort')} {priority}
                          </span>
                        )}
                        {replies[email.id]?.trim() && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-700 flex items-center gap-1">
                            <Reply size={9} />
                            {t('workspace.replied')}
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Reading pane */}
              <div className="flex-1 p-4 overflow-y-auto max-h-[420px]">
                {activeEmail ? (
                  <div className="space-y-3">
                    <div>
                      <h4 className="font-semibold text-gray-900 text-sm">{activeEmail.subject}</h4>
                      <div className="text-xs text-gray-500 mt-1">
                        {activeEmail.from_name} &lt;{activeEmail.from_email}&gt; · {activeEmail.received}
                      </div>
                    </div>
                    <div className="text-sm text-gray-800 whitespace-pre-wrap border-t border-gray-100 pt-3">
                      {activeEmail.body}
                    </div>

                    {/* Priority selector */}
                    <div className="flex items-center gap-2 pt-2">
                      <label className="text-xs text-gray-600">{t('workspace.setPriority')}</label>
                      <select
                        value={priorities[activeEmail.id] || 0}
                        onChange={(e) =>
                          setPriorities((prev) => ({ ...prev, [activeEmail.id]: Number(e.target.value) }))
                        }
                        className="input text-xs py-1 w-24"
                      >
                        <option value={0}>—</option>
                        {emails.map((_, i) => (
                          <option key={i} value={i + 1}>
                            {i + 1}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Reply box */}
                    <div className="border border-gray-200 rounded-lg p-3 space-y-2 bg-gray-50">
                      <div className="flex items-center gap-2 text-xs font-medium text-gray-700">
                        <Reply size={13} />
                        {t('workspace.reply')}
                      </div>
                      <textarea
                        value={replies[activeEmail.id] || ''}
                        onChange={(e) => setReplies((prev) => ({ ...prev, [activeEmail.id]: e.target.value }))}
                        rows={4}
                        className="input text-sm w-full"
                        placeholder={t('workspace.replyPlaceholder') as string}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-gray-400 flex items-center justify-center h-full">
                    {t('workspace.selectEmail')}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Overall approach */}
          <div className="card space-y-2">
            <label className="text-sm font-medium text-gray-800">
              {t('workspace.inboxApproach')}
            </label>
            <textarea
              value={answerText}
              onChange={(e) => setAnswerText(e.target.value)}
              rows={2}
              className="input text-sm w-full"
              placeholder={t('workspace.inboxApproachPlaceholder') as string}
            />
          </div>
          {extras}
        </div>
      </div>
      <SubmitBar
        onSubmit={handleSubmit}
        submitting={submitting}
        disabled={repliesCount === 0}
        hint={t('workspace.inboxHint') as string}
      />
    </div>
  );
};

// ============================================================================
// 4) Data Notebook (Jupyter look)
// ============================================================================

const NotebookWorkspace: React.FC<WorkspaceSimulationProps> = ({ task, submitting, onSubmit, extras }) => {
  const { t } = useTranslation();
  const cells: any[] = Array.isArray(task.cells) ? task.cells : [];
  const questions: string[] = Array.isArray(task.questions) ? task.questions : [];
  const [answers, setAnswers] = useState<string[]>(questions.map(() => ''));

  const handleSubmit = () => {
    onSubmit({ answers: questions.map((q, i) => ({ question: q, answer: answers[i] || '' })) });
  };

  let codeIndex = 0;

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="flex-1 overflow-y-auto p-4">
        <div className="max-w-5xl mx-auto space-y-4">
          <TaskHeader
            icon={<BookOpen size={20} className="text-orange-600" />}
            title={task.task_title}
            description={task.task_description}
            accent="bg-orange-50 border-orange-200"
          />

          {/* Notebook frame */}
          <div className="rounded-lg overflow-hidden border border-gray-300 shadow bg-white">
            <div className="flex items-center gap-2 px-3 py-2 bg-[#f37726] text-white text-xs">
              <BookOpen size={14} />
              <span className="font-medium truncate">{task.task_title || 'analysis'}.ipynb</span>
            </div>
            <div className="divide-y divide-gray-100">
              {cells.map((cell, i) => {
                const isCode = cell.cell_type === 'code';
                const execNumber = isCode ? ++codeIndex : null;
                return (
                  <div key={i} className="flex gap-0">
                    <div className="w-16 flex-shrink-0 text-right pr-2 pt-3 text-[11px] font-mono text-blue-600 select-none">
                      {isCode ? `In [${execNumber}]:` : ''}
                    </div>
                    <div className="flex-1 min-w-0 py-2 pr-3">
                      {isCode ? (
                        <>
                          <pre className="bg-gray-50 border border-gray-200 rounded p-2.5 text-xs font-mono text-gray-800 overflow-x-auto whitespace-pre">
                            {cell.source}
                          </pre>
                          {cell.output && (
                            <pre className="mt-1 p-2.5 text-xs font-mono text-gray-700 overflow-x-auto whitespace-pre-wrap border-l-2 border-gray-200 bg-white">
                              {cell.output}
                            </pre>
                          )}
                        </>
                      ) : (
                        <div className="text-sm text-gray-800 whitespace-pre-wrap py-1">{cell.source}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Questions */}
          <div className="card space-y-3">
            <h4 className="text-sm font-semibold text-gray-900">
              {t('workspace.analysisQuestions')}
            </h4>
            <QuestionAnswers
              questions={questions}
              answers={answers}
              onChange={(i, v) => setAnswers((prev) => prev.map((a, idx) => (idx === i ? v : a)))}
            />
          </div>
          {extras}
        </div>
      </div>
      <SubmitBar
        onSubmit={handleSubmit}
        submitting={submitting}
        disabled={answers.every((a) => !a.trim())}
        hint={t('workspace.notebookHint') as string}
      />
    </div>
  );
};

// ============================================================================
// 5) SCADA Panel (industrial HMI look)
// ============================================================================

const unitStatusStyles: Record<string, string> = {
  running: 'bg-green-500',
  stopped: 'bg-gray-400',
  fault: 'bg-red-500 animate-pulse',
  warning: 'bg-amber-400',
};

const severityStyles: Record<string, { icon: React.ReactNode; cls: string }> = {
  critical: { icon: <AlertTriangle size={13} />, cls: 'text-red-400' },
  warning: { icon: <AlertCircle size={13} />, cls: 'text-amber-400' },
  info: { icon: <Info size={13} />, cls: 'text-sky-400' },
};

const isOutOfRange = (value: string, range: string): boolean => {
  const v = parseFloat(String(value).replace(',', '.'));
  const m = String(range || '').match(/(-?\d+(?:[.,]\d+)?)\s*[-–]\s*(-?\d+(?:[.,]\d+)?)/);
  if (!Number.isFinite(v) || !m) return false;
  const lo = parseFloat(m[1].replace(',', '.'));
  const hi = parseFloat(m[2].replace(',', '.'));
  return v < lo || v > hi;
};

const ScadaWorkspace: React.FC<WorkspaceSimulationProps> = ({ task, submitting, onSubmit, extras }) => {
  const { t } = useTranslation();
  const units: any[] = Array.isArray(task.process_units) ? task.process_units : [];
  const alarms: any[] = Array.isArray(task.alarms) ? task.alarms : [];
  const questions: string[] = Array.isArray(task.questions) ? task.questions : [];
  const [answers, setAnswers] = useState<string[]>(questions.map(() => ''));

  const handleSubmit = () => {
    onSubmit({ answers: questions.map((q, i) => ({ question: q, answer: answers[i] || '' })) });
  };

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="flex-1 overflow-y-auto p-4">
        <div className="max-w-6xl mx-auto space-y-4">
          <TaskHeader
            icon={<Gauge size={20} className="text-cyan-700" />}
            title={task.task_title}
            description={task.task_description}
            accent="bg-cyan-50 border-cyan-200"
          />

          {/* SCADA frame */}
          <div className="rounded-lg overflow-hidden border border-slate-700 shadow-lg bg-slate-900 text-slate-200">
            <div className="flex items-center gap-2 px-3 py-2 bg-slate-800 text-xs border-b border-slate-700">
              <Gauge size={14} className="text-cyan-400" />
              <span className="font-medium">SCADA — {t('workspace.processOverview')}</span>
              <span className="ml-auto flex items-center gap-1.5 text-[10px]">
                <span className="w-2 h-2 rounded-full bg-green-500" /> ONLINE
              </span>
            </div>

            {/* Units grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 p-4">
              {units.map((unit) => (
                <div
                  key={unit.id}
                  className={`rounded border p-3 ${
                    unit.status === 'fault'
                      ? 'border-red-500/60 bg-red-950/30'
                      : unit.status === 'warning'
                        ? 'border-amber-500/60 bg-amber-950/20'
                        : 'border-slate-700 bg-slate-800/60'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${unitStatusStyles[unit.status] || 'bg-gray-400'}`} />
                    <span className="text-xs font-semibold truncate">{unit.name}</span>
                    <span className="ml-auto text-[9px] uppercase tracking-wider text-slate-400">{unit.status}</span>
                  </div>
                  <div className="mt-2 space-y-1">
                    {(Array.isArray(unit.readings) ? unit.readings : []).map((reading: any, ri: number) => {
                      const bad = isOutOfRange(reading.value, reading.normal_range);
                      return (
                        <div key={ri} className="flex items-baseline justify-between text-[11px]">
                          <span className="text-slate-400">{reading.label}</span>
                          <span className={`font-mono font-semibold ${bad ? 'text-red-400' : 'text-green-300'}`}>
                            {reading.value} {reading.unit}
                            <span className="ml-1.5 text-[9px] text-slate-500 font-normal">({reading.normal_range})</span>
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {/* Alarm list */}
            <div className="border-t border-slate-700">
              <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-slate-500 bg-slate-800">
                {t('workspace.alarms')}
              </div>
              <div className="max-h-44 overflow-y-auto divide-y divide-slate-800">
                {alarms.map((alarm) => {
                  const sev = severityStyles[alarm.severity] || severityStyles.info;
                  return (
                    <div key={alarm.id} className="flex items-center gap-2 px-3 py-1.5 text-xs font-mono">
                      <span className="text-slate-500">{alarm.time}</span>
                      <span className={`flex items-center gap-1 ${sev.cls}`}>
                        {sev.icon}
                        <span className="uppercase text-[9px]">{alarm.severity}</span>
                      </span>
                      <span className="text-slate-400">[{alarm.unit_id}]</span>
                      <span className="text-slate-200 truncate">{alarm.message}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Questions */}
          <div className="card space-y-3">
            <h4 className="text-sm font-semibold text-gray-900">
              {t('workspace.diagnosisQuestions')}
            </h4>
            <QuestionAnswers
              questions={questions}
              answers={answers}
              onChange={(i, v) => setAnswers((prev) => prev.map((a, idx) => (idx === i ? v : a)))}
            />
          </div>
          {extras}
        </div>
      </div>
      <SubmitBar
        onSubmit={handleSubmit}
        submitting={submitting}
        disabled={answers.every((a) => !a.trim())}
        hint={t('workspace.scadaHint') as string}
      />
    </div>
  );
};

// ============================================================================
// 6) Design Review (CAD look)
// ============================================================================

const DesignReviewWorkspace: React.FC<WorkspaceSimulationProps> = ({ task, submitting, onSubmit, extras }) => {
  const { t } = useTranslation();
  const drawing = task.drawing || {};
  const parts: any[] = Array.isArray(drawing.parts) ? drawing.parts : [];
  const specs: any[] = Array.isArray(drawing.specs) ? drawing.specs : [];
  const reviewPoints: string[] = Array.isArray(task.review_points) ? task.review_points : [];
  const questions: string[] = Array.isArray(task.questions) ? task.questions : [];
  const [answers, setAnswers] = useState<string[]>(questions.map(() => ''));

  const handleSubmit = () => {
    onSubmit({ answers: questions.map((q, i) => ({ question: q, answer: answers[i] || '' })) });
  };

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="flex-1 overflow-y-auto p-4">
        <div className="max-w-6xl mx-auto space-y-4">
          <TaskHeader
            icon={<DraftingCompass size={20} className="text-blue-700" />}
            title={task.task_title}
            description={task.task_description}
            accent="bg-blue-50 border-blue-200"
          />

          {/* CAD frame */}
          <div className="rounded-lg overflow-hidden border border-gray-400 shadow-lg bg-[#2b2b2b] text-gray-200">
            <div className="flex items-center gap-2 px-3 py-2 bg-[#1f1f1f] text-xs border-b border-gray-700">
              <DraftingCompass size={14} className="text-blue-400" />
              <span className="font-medium truncate">{drawing.title || task.task_title || 'Assembly.SLDASM'}</span>
              <span className="ml-auto text-[10px] text-gray-500">{t('workspace.designReviewMode')}</span>
            </div>

            <div className="grid md:grid-cols-2 gap-0">
              {/* Parts table (BOM) */}
              <div className="border-r border-gray-700">
                <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-gray-500 bg-[#232323]">
                  {t('workspace.bom')}
                </div>
                <div className="divide-y divide-gray-800 max-h-80 overflow-y-auto">
                  {parts.map((part) => (
                    <div key={part.id} className="px-3 py-2 text-xs">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-blue-400">{part.id}</span>
                        <span className="font-semibold text-gray-100">{part.name}</span>
                      </div>
                      <div className="mt-1 grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px] text-gray-400">
                        <span>{t('workspace.material')}: <span className="text-gray-200">{part.material}</span></span>
                        <span>{t('workspace.dimensions')}: <span className="text-gray-200 font-mono">{part.dimensions}</span></span>
                      </div>
                      {part.note && <div className="mt-0.5 text-[11px] text-amber-300/80">{part.note}</div>}
                    </div>
                  ))}
                </div>
              </div>

              {/* Specs */}
              <div>
                <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-gray-500 bg-[#232323]">
                  {t('workspace.specs')}
                </div>
                <div className="p-3 space-y-1.5 max-h-80 overflow-y-auto">
                  {specs.map((spec, i) => (
                    <div key={i} className="flex justify-between gap-3 text-xs border-b border-gray-800 pb-1.5">
                      <span className="text-gray-400">{spec.label}</span>
                      <span className="text-gray-100 font-mono text-right">{spec.value}</span>
                    </div>
                  ))}
                  {reviewPoints.length > 0 && (
                    <div className="pt-2">
                      <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1.5">
                        {t('workspace.reviewChecklist')}
                      </div>
                      <ul className="space-y-1">
                        {reviewPoints.map((point, i) => (
                          <li key={i} className="text-[11px] text-gray-300 flex gap-1.5">
                            <span className="text-blue-400 flex-shrink-0">▸</span>
                            {point}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Questions */}
          <div className="card space-y-3">
            <h4 className="text-sm font-semibold text-gray-900">
              {t('workspace.engineeringAssessment')}
            </h4>
            <QuestionAnswers
              questions={questions}
              answers={answers}
              onChange={(i, v) => setAnswers((prev) => prev.map((a, idx) => (idx === i ? v : a)))}
            />
          </div>
          {extras}
        </div>
      </div>
      <SubmitBar
        onSubmit={handleSubmit}
        submitting={submitting}
        disabled={answers.every((a) => !a.trim())}
        hint={t('workspace.designHint') as string}
      />
    </div>
  );
};

// ============================================================================
// 7) CRM Pipeline (kanban look)
// ============================================================================

const STAGE_ORDER = ['lead', 'meeting', 'proposal', 'negotiation', 'closing'];

const CrmWorkspace: React.FC<WorkspaceSimulationProps> = ({ task, submitting, onSubmit, extras }) => {
  const { t } = useTranslation();
  const deals: any[] = Array.isArray(task.deals) ? task.deals : [];
  const questions: string[] = Array.isArray(task.questions) ? task.questions : [];
  const [answers, setAnswers] = useState<string[]>(questions.map(() => ''));

  const stageLabels: Record<string, string> = {
    lead: t('workspace.stageLead'),
    meeting: t('workspace.stageMeeting'),
    proposal: t('workspace.stageProposal'),
    negotiation: t('workspace.stageNegotiation'),
    closing: t('workspace.stageClosing'),
  };

  const stages = STAGE_ORDER.filter((s) => deals.some((d) => d.stage === s));

  const handleSubmit = () => {
    onSubmit({ answers: questions.map((q, i) => ({ question: q, answer: answers[i] || '' })) });
  };

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="flex-1 overflow-y-auto p-4">
        <div className="max-w-6xl mx-auto space-y-4">
          <TaskHeader
            icon={<TrendingUp size={20} className="text-violet-600" />}
            title={task.task_title}
            description={task.task_description}
            accent="bg-violet-50 border-violet-200"
          />

          {/* CRM frame */}
          <div className="rounded-lg overflow-hidden border border-gray-300 shadow bg-gray-50">
            <div className="flex items-center gap-2 px-3 py-2 bg-violet-700 text-white text-xs">
              <TrendingUp size={14} />
              <span className="font-medium">{t('workspace.pipeline')}</span>
              <span className="ml-auto opacity-80">{deals.length} {t('workspace.deals')}</span>
            </div>
            <div className="flex gap-3 p-3 overflow-x-auto">
              {stages.map((stage) => (
                <div key={stage} className="w-60 flex-shrink-0">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 px-1 pb-2">
                    {stageLabels[stage] || stage}
                    <span className="ml-1.5 text-gray-400">({deals.filter((d) => d.stage === stage).length})</span>
                  </div>
                  <div className="space-y-2">
                    {deals
                      .filter((d) => d.stage === stage)
                      .map((deal) => (
                        <div key={deal.id} className="bg-white rounded-lg border border-gray-200 p-2.5 shadow-sm text-xs">
                          <div className="font-semibold text-gray-900 truncate">{deal.company}</div>
                          <div className="text-gray-500 mt-0.5">{deal.contact}</div>
                          <div className="font-mono font-semibold text-violet-700 mt-1">{deal.value}</div>
                          <div className="text-[10px] text-gray-400 mt-1">{deal.last_activity}</div>
                          {deal.notes && (
                            <div className="mt-1.5 text-[11px] text-gray-600 bg-gray-50 rounded p-1.5 whitespace-pre-wrap">
                              {deal.notes}
                            </div>
                          )}
                        </div>
                      ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Questions */}
          <div className="card space-y-3">
            <h4 className="text-sm font-semibold text-gray-900">
              {t('workspace.salesAssessment')}
            </h4>
            <QuestionAnswers
              questions={questions}
              answers={answers}
              onChange={(i, v) => setAnswers((prev) => prev.map((a, idx) => (idx === i ? v : a)))}
            />
          </div>
          {extras}
        </div>
      </div>
      <SubmitBar
        onSubmit={handleSubmit}
        submitting={submitting}
        disabled={answers.every((a) => !a.trim())}
        hint={t('workspace.crmHint') as string}
      />
    </div>
  );
};

// ============================================================================
// 8) Legal Review (contract look)
// ============================================================================

const LegalWorkspace: React.FC<WorkspaceSimulationProps> = ({ task, submitting, onSubmit, extras }) => {
  const { t } = useTranslation();
  const doc = task.document || {};
  const clauses: any[] = Array.isArray(doc.clauses) ? doc.clauses : [];
  const questions: string[] = Array.isArray(task.questions) ? task.questions : [];
  const [answers, setAnswers] = useState<string[]>(questions.map(() => ''));
  const [flags, setFlags] = useState<Record<string, string>>({});
  const [flaggedIds, setFlaggedIds] = useState<Set<string>>(new Set());

  const toggleFlag = (id: string) => {
    setFlaggedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSubmit = () => {
    onSubmit({
      flagged_clauses: Array.from(flaggedIds).map((clause_id) => ({
        clause_id,
        comment: (flags[clause_id] || '').trim(),
      })),
      answers: questions.map((q, i) => ({ question: q, answer: answers[i] || '' })),
    });
  };

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="flex-1 overflow-y-auto p-4">
        <div className="max-w-4xl mx-auto space-y-4">
          <TaskHeader
            icon={<Scale size={20} className="text-stone-700" />}
            title={task.task_title}
            description={task.task_description}
            accent="bg-stone-50 border-stone-300"
          />

          {/* Document frame */}
          <div className="rounded-lg overflow-hidden border border-stone-300 shadow bg-white">
            <div className="px-6 py-4 border-b border-stone-200 bg-stone-50 text-center">
              <h4 className="font-serif font-semibold text-gray-900">{doc.title || task.task_title}</h4>
              <p className="text-[11px] text-stone-500 mt-1">
                {t('workspace.legalFlagHint')}
              </p>
            </div>
            <div className="divide-y divide-stone-100">
              {clauses.map((clause) => {
                const flagged = flaggedIds.has(clause.id);
                return (
                  <div key={clause.id} className={`px-6 py-4 ${flagged ? 'bg-red-50/60' : ''}`}>
                    <div className="flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-gray-900 font-serif">
                          {clause.id}. {clause.heading}
                        </div>
                        <p className="text-sm text-gray-700 mt-1.5 leading-relaxed font-serif whitespace-pre-wrap">
                          {clause.text}
                        </p>
                      </div>
                      <button
                        onClick={() => toggleFlag(clause.id)}
                        className={`flex-shrink-0 flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs transition-colors ${
                          flagged
                            ? 'bg-red-600 text-white'
                            : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                        }`}
                      >
                        <Flag size={12} />
                        {flagged ? t('workspace.flagged') : t('workspace.flagRisk')}
                      </button>
                    </div>
                    {flagged && (
                      <textarea
                        value={flags[clause.id] || ''}
                        onChange={(e) => setFlags((prev) => ({ ...prev, [clause.id]: e.target.value }))}
                        rows={2}
                        className="input text-sm w-full mt-2"
                        placeholder={t('workspace.redlinePlaceholder') as string}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Questions */}
          <div className="card space-y-3">
            <h4 className="text-sm font-semibold text-gray-900">
              {t('workspace.legalAssessment')}
            </h4>
            <QuestionAnswers
              questions={questions}
              answers={answers}
              onChange={(i, v) => setAnswers((prev) => prev.map((a, idx) => (idx === i ? v : a)))}
            />
          </div>
          {extras}
        </div>
      </div>
      <SubmitBar
        onSubmit={handleSubmit}
        submitting={submitting}
        disabled={flaggedIds.size === 0 && answers.every((a) => !a.trim())}
        hint={t('workspace.legalHint') as string}
      />
    </div>
  );
};

// ============================================================================
// 9) G-Code Console (CNC controller look)
// ============================================================================

const GcodeWorkspace: React.FC<WorkspaceSimulationProps> = ({ task, submitting, onSubmit, extras }) => {
  const { t } = useTranslation();
  const tools: any[] = Array.isArray(task.tools) ? task.tools : [];
  const questions: string[] = Array.isArray(task.questions) ? task.questions : [];
  const [gcode, setGcode] = useState<string>(String(task.gcode || ''));
  const [answers, setAnswers] = useState<string[]>(questions.map(() => ''));

  const handleSubmit = () => {
    onSubmit({
      gcode,
      answers: questions.map((q, i) => ({ question: q, answer: answers[i] || '' })),
    });
  };

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="flex-1 overflow-y-auto p-4">
        <div className="max-w-6xl mx-auto space-y-4">
          <TaskHeader
            icon={<Wrench size={20} className="text-orange-700" />}
            title={task.task_title}
            description={task.task_description}
            accent="bg-orange-50 border-orange-200"
          />

          {/* CNC controller frame */}
          <div className="rounded-lg overflow-hidden border border-zinc-700 shadow-lg bg-zinc-900 text-zinc-200">
            <div className="flex items-center gap-2 px-3 py-2 bg-zinc-800 text-xs border-b border-zinc-700">
              <Wrench size={14} className="text-orange-400" />
              <span className="font-medium">{task.machine || 'CNC'}</span>
              <span className="ml-auto flex items-center gap-1.5 text-[10px]">
                <span className="w-2 h-2 rounded-full bg-green-500" /> {t('workspace.ready')}
              </span>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-0">
              {/* G-code editor */}
              <div className="lg:col-span-2 border-r border-zinc-700">
                <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-zinc-500 bg-zinc-800/60">
                  {t('workspace.gcodeProgram')}
                </div>
                <textarea
                  value={gcode}
                  onChange={(e) => setGcode(e.target.value)}
                  spellCheck={false}
                  className="w-full bg-zinc-900 text-green-300 font-mono text-xs p-3 outline-none resize-y"
                  style={{ minHeight: 340 }}
                />
              </div>

              {/* Machine info */}
              <div className="text-xs">
                <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-zinc-500 bg-zinc-800/60">
                  {t('workspace.stockMaterial')}
                </div>
                <div className="px-3 py-2 text-zinc-300 border-b border-zinc-800">{task.stock || '-'}</div>
                <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-zinc-500 bg-zinc-800/60">
                  {t('workspace.toolTable')}
                </div>
                <div className="divide-y divide-zinc-800">
                  {tools.map((tool, i) => (
                    <div key={i} className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-semibold text-orange-300">{tool.number}</span>
                        <span className="text-zinc-300 truncate">{tool.name}</span>
                      </div>
                      {tool.note && <div className="text-[10px] text-zinc-500 mt-0.5">{tool.note}</div>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Questions */}
          <div className="card space-y-3">
            <h4 className="text-sm font-semibold text-gray-900">
              {t('workspace.gcodeQuestions')}
            </h4>
            <QuestionAnswers
              questions={questions}
              answers={answers}
              onChange={(i, v) => setAnswers((prev) => prev.map((a, idx) => (idx === i ? v : a)))}
            />
          </div>
          {extras}
        </div>
      </div>
      <SubmitBar
        onSubmit={handleSubmit}
        submitting={submitting}
        hint={t('workspace.gcodeHint') as string}
      />
    </div>
  );
};

// ============================================================================
// 10) Embedded Workbench (firmware IDE + serial monitor)
// ============================================================================

const EmbeddedWorkspace: React.FC<WorkspaceSimulationProps> = ({ task, submitting, onSubmit, extras }) => {
  const { t } = useTranslation();
  const initialFiles = useMemo(
    () =>
      (Array.isArray(task.files) ? task.files : []).map((f: any) => ({
        name: String(f?.name || 'main.c'),
        content: String(f?.content || ''),
        readonly: f?.readonly === true,
      })),
    [task]
  );
  const datasheetNotes: string[] = Array.isArray(task.datasheet_notes) ? task.datasheet_notes : [];
  const questions: string[] = Array.isArray(task.questions) ? task.questions : [];
  const [fileContents, setFileContents] = useState<string[]>(initialFiles.map((f) => f.content));
  const [activeFile, setActiveFile] = useState(0);
  const [answers, setAnswers] = useState<string[]>(questions.map(() => ''));

  const handleSubmit = () => {
    onSubmit({
      files: initialFiles.map((f, i) => ({ name: f.name, content: fileContents[i] ?? f.content })),
      answers: questions.map((q, i) => ({ question: q, answer: answers[i] || '' })),
    });
  };

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="flex-1 overflow-y-auto p-4">
        <div className="max-w-6xl mx-auto space-y-4">
          <TaskHeader
            icon={<Cpu size={20} className="text-emerald-700" />}
            title={task.task_title}
            description={task.task_description}
            accent="bg-emerald-50 border-emerald-200"
          />

          {/* Firmware IDE frame */}
          <div className="rounded-lg overflow-hidden border border-gray-800 shadow-lg bg-[#1e1e1e] text-gray-200">
            <div className="flex items-center gap-2 px-3 py-2 bg-[#323233] text-xs text-gray-400">
              <Cpu size={14} className="text-emerald-400" />
              <span className="font-medium text-gray-300">{task.mcu || 'MCU'}</span>
              <span className="ml-auto text-[10px]">{t('workspace.firmwareWorkbench')}</span>
            </div>

            {/* Tabs */}
            <div className="flex bg-[#252526] border-b border-[#3c3c3c] overflow-x-auto">
              {initialFiles.map((f, i) => (
                <button
                  key={f.name + i}
                  onClick={() => setActiveFile(i)}
                  className={`px-4 py-2 text-xs whitespace-nowrap border-r border-[#3c3c3c] ${
                    i === activeFile ? 'bg-[#1e1e1e] text-white' : 'text-gray-400 hover:bg-[#2a2d2e]'
                  }`}
                >
                  {f.name}
                  {f.readonly && <span className="ml-1.5 text-[9px] text-gray-500">{t('workspace.readonly')}</span>}
                </button>
              ))}
            </div>

            <textarea
              value={fileContents[activeFile] ?? ''}
              onChange={(e) =>
                setFileContents((prev) => prev.map((c, idx) => (idx === activeFile ? e.target.value : c)))
              }
              readOnly={initialFiles[activeFile]?.readonly}
              spellCheck={false}
              className="w-full bg-[#1e1e1e] text-gray-200 font-mono text-xs p-3 outline-none resize-y"
              style={{ minHeight: 300 }}
            />

            {/* Serial monitor */}
            {task.serial_output && (
              <div className="border-t border-[#3c3c3c]">
                <div className="flex items-center gap-2 px-3 py-1.5 text-[10px] uppercase tracking-wider text-gray-500 bg-[#252526]">
                  <Terminal size={11} />
                  {t('workspace.serialMonitor')}
                </div>
                <pre className="p-3 text-xs font-mono text-amber-300 whitespace-pre-wrap max-h-36 overflow-y-auto">
                  {task.serial_output}
                </pre>
              </div>
            )}
          </div>

          {/* Datasheet notes */}
          {datasheetNotes.length > 0 && (
            <div className="card">
              <h4 className="text-sm font-semibold text-gray-900 mb-2">
                {t('workspace.datasheetNotes')}
              </h4>
              <ul className="space-y-1 text-xs text-gray-700 font-mono">
                {datasheetNotes.map((note, i) => (
                  <li key={i} className="flex gap-2">
                    <Info size={12} className="flex-shrink-0 mt-0.5 text-emerald-600" />
                    {note}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Questions */}
          {questions.length > 0 && (
            <div className="card space-y-3">
              <h4 className="text-sm font-semibold text-gray-900">
                {t('workspace.embeddedQuestions')}
              </h4>
              <QuestionAnswers
                questions={questions}
                answers={answers}
                onChange={(i, v) => setAnswers((prev) => prev.map((a, idx) => (idx === i ? v : a)))}
              />
            </div>
          )}
          {extras}
        </div>
      </div>
      <SubmitBar
        onSubmit={handleSubmit}
        submitting={submitting}
        hint={t('workspace.embeddedHint') as string}
      />
    </div>
  );
};

// ============================================================================
// 11) Circuit Designer (schematic review)
// ============================================================================

const CircuitWorkspace: React.FC<WorkspaceSimulationProps> = ({ task, submitting, onSubmit, extras }) => {
  const { t } = useTranslation();
  const circuit = task.circuit || {};
  const components: any[] = Array.isArray(circuit.components) ? circuit.components : [];
  const connections: string[] = Array.isArray(circuit.connections) ? circuit.connections : [];
  const specs: any[] = Array.isArray(task.specs) ? task.specs : [];
  const questions: string[] = Array.isArray(task.questions) ? task.questions : [];
  const [values, setValues] = useState<string[]>(components.map((c) => String(c.value || '')));
  const [answers, setAnswers] = useState<string[]>(questions.map(() => ''));

  const handleSubmit = () => {
    onSubmit({
      component_changes: components
        .map((c, i) => ({ ref: c.ref, old_value: c.value, new_value: values[i] }))
        .filter((c) => String(c.old_value || '') !== String(c.new_value || '')),
      answers: questions.map((q, i) => ({ question: q, answer: answers[i] || '' })),
    });
  };

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="flex-1 overflow-y-auto p-4">
        <div className="max-w-6xl mx-auto space-y-4">
          <TaskHeader
            icon={<Zap size={20} className="text-yellow-600" />}
            title={task.task_title}
            description={task.task_description}
            accent="bg-yellow-50 border-yellow-200"
          />

          {/* Schematic frame */}
          <div className="rounded-lg overflow-hidden border border-gray-300 shadow bg-white">
            <div className="flex items-center gap-2 px-3 py-2 bg-gray-100 text-xs border-b border-gray-200">
              <Zap size={14} className="text-yellow-600" />
              <span className="font-medium text-gray-800">{circuit.title || task.task_title}</span>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-gray-200">
              {/* Component table (editable values) */}
              <div>
                <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-gray-500 bg-gray-50">
                  {t('workspace.components')}
                </div>
                <table className="w-full text-xs">
                  <tbody className="divide-y divide-gray-100">
                    {components.map((c, i) => (
                      <tr key={i}>
                        <td className="px-3 py-2 font-mono font-semibold text-gray-900 w-12">{c.ref}</td>
                        <td className="px-3 py-2 text-gray-600">{c.type}</td>
                        <td className="px-3 py-2 w-36">
                          <input
                            value={values[i] ?? ''}
                            onChange={(e) =>
                              setValues((prev) => prev.map((v, idx) => (idx === i ? e.target.value : v)))
                            }
                            className={`input text-xs w-full font-mono py-1 ${
                              String(components[i]?.value || '') !== String(values[i] || '')
                                ? 'border-yellow-500 bg-yellow-50'
                                : ''
                            }`}
                          />
                        </td>
                        <td className="px-3 py-2 text-[10px] text-gray-400">{c.note}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Connections + specs */}
              <div>
                <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-gray-500 bg-gray-50">
                  {t('workspace.connections')}
                </div>
                <ul className="px-3 py-2 space-y-1 text-xs font-mono text-gray-700">
                  {connections.map((conn, i) => (
                    <li key={i}>• {conn}</li>
                  ))}
                </ul>
                {specs.length > 0 && (
                  <>
                    <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-gray-500 bg-gray-50">
                      {t('workspace.specs')}
                    </div>
                    <div className="px-3 py-2 space-y-1 text-xs">
                      {specs.map((s, i) => (
                        <div key={i} className="flex justify-between gap-3">
                          <span className="text-gray-500">{s.label}</span>
                          <span className="font-medium text-gray-900">{s.value}</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Questions */}
          <div className="card space-y-3">
            <h4 className="text-sm font-semibold text-gray-900">
              {t('workspace.circuitQuestions')}
            </h4>
            <QuestionAnswers
              questions={questions}
              answers={answers}
              onChange={(i, v) => setAnswers((prev) => prev.map((a, idx) => (idx === i ? v : a)))}
            />
          </div>
          {extras}
        </div>
      </div>
      <SubmitBar
        onSubmit={handleSubmit}
        submitting={submitting}
        disabled={answers.every((a) => !a.trim())}
        hint={t('workspace.circuitHint') as string}
      />
    </div>
  );
};

// ============================================================================
// 12) PLC Ladder Editor
// ============================================================================

const PlcLadderWorkspace: React.FC<WorkspaceSimulationProps> = ({ task, submitting, onSubmit, extras }) => {
  const { t } = useTranslation();
  const ioTable: any[] = Array.isArray(task.io_table) ? task.io_table : [];
  const initialRungs: any[] = Array.isArray(task.rungs) ? task.rungs : [];
  const questions: string[] = Array.isArray(task.questions) ? task.questions : [];
  const [rungLogic, setRungLogic] = useState<string[]>(initialRungs.map((r) => String(r.logic || '')));
  const [answers, setAnswers] = useState<string[]>(questions.map(() => ''));

  const handleSubmit = () => {
    onSubmit({
      rungs: initialRungs.map((r, i) => ({ number: r.number, logic: rungLogic[i] ?? r.logic })),
      answers: questions.map((q, i) => ({ question: q, answer: answers[i] || '' })),
    });
  };

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="flex-1 overflow-y-auto p-4">
        <div className="max-w-6xl mx-auto space-y-4">
          <TaskHeader
            icon={<Gauge size={20} className="text-lime-700" />}
            title={task.task_title}
            description={task.task_description}
            accent="bg-lime-50 border-lime-200"
          />

          {/* PLC frame */}
          <div className="rounded-lg overflow-hidden border border-gray-700 shadow-lg bg-gray-900 text-gray-200">
            <div className="flex items-center gap-2 px-3 py-2 bg-gray-800 text-xs border-b border-gray-700">
              <Gauge size={14} className="text-lime-400" />
              <span className="font-medium">{t('workspace.plcLadderEditor')}</span>
              <span className="ml-auto flex items-center gap-1.5 text-[10px]">
                <span className="w-2 h-2 rounded-full bg-green-500" /> {t('workspace.run')}
              </span>
            </div>

            {/* I/O table */}
            <div className="border-b border-gray-700">
              <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-gray-500 bg-gray-800/60">
                {t('workspace.ioTable')}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 px-3 py-2">
                {ioTable.map((io, i) => (
                  <div key={i} className="flex items-baseline gap-2 text-xs py-0.5">
                    <span className="font-mono font-semibold text-lime-300 w-12 flex-shrink-0">{io.address}</span>
                    <span className="font-mono text-gray-300 w-24 flex-shrink-0 truncate">{io.tag}</span>
                    <span className="text-gray-500 text-[11px] truncate">{io.description}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Rungs (editable) */}
            <div className="divide-y divide-gray-800">
              {initialRungs.map((rung, i) => (
                <div key={i} className="px-3 py-2">
                  <div className="flex items-center gap-2 text-[10px] text-gray-500 mb-1">
                    <span className="font-mono">{t('workspace.rung')} {rung.number}</span>
                    {rung.comment && <span className="text-gray-600">— {rung.comment}</span>}
                  </div>
                  <input
                    value={rungLogic[i] ?? ''}
                    onChange={(e) =>
                      setRungLogic((prev) => prev.map((r, idx) => (idx === i ? e.target.value : r)))
                    }
                    spellCheck={false}
                    className={`w-full bg-gray-950 border rounded px-2 py-1.5 text-xs font-mono outline-none ${
                      String(initialRungs[i]?.logic || '') !== String(rungLogic[i] || '')
                        ? 'border-lime-500 text-lime-200'
                        : 'border-gray-700 text-green-300'
                    }`}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Questions */}
          <div className="card space-y-3">
            <h4 className="text-sm font-semibold text-gray-900">
              {t('workspace.plcQuestions')}
            </h4>
            <QuestionAnswers
              questions={questions}
              answers={answers}
              onChange={(i, v) => setAnswers((prev) => prev.map((a, idx) => (idx === i ? v : a)))}
            />
          </div>
          {extras}
        </div>
      </div>
      <SubmitBar
        onSubmit={handleSubmit}
        submitting={submitting}
        hint={t('workspace.plcHint') as string}
      />
    </div>
  );
};

// ============================================================================
// 13) SQL Console
// ============================================================================

const SqlConsoleWorkspace: React.FC<WorkspaceSimulationProps> = ({ task, submitting, onSubmit, extras }) => {
  const { t } = useTranslation();
  const dbSchema: any[] = Array.isArray(task.db_schema) ? task.db_schema : [];
  const sampleData: any[] = Array.isArray(task.sample_data) ? task.sample_data : [];
  const queryTasks: string[] = Array.isArray(task.query_tasks) ? task.query_tasks : [];
  const [queries, setQueries] = useState<string[]>(queryTasks.map(() => ''));

  const handleSubmit = () => {
    onSubmit({
      queries: queryTasks.map((qt, i) => ({ task: qt, sql: queries[i] || '' })),
    });
  };

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="flex-1 overflow-y-auto p-4">
        <div className="max-w-6xl mx-auto space-y-4">
          <TaskHeader
            icon={<Database size={20} className="text-sky-700" />}
            title={task.task_title}
            description={task.task_description}
            accent="bg-sky-50 border-sky-200"
          />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Schema panel */}
            <div className="rounded-lg overflow-hidden border border-gray-300 shadow bg-white h-fit">
              <div className="flex items-center gap-2 px-3 py-2 bg-gray-100 text-xs border-b border-gray-200">
                <Database size={13} className="text-sky-600" />
                <span className="font-medium text-gray-800">{t('workspace.dbSchema')}</span>
              </div>
              <div className="divide-y divide-gray-100">
                {dbSchema.map((tbl, i) => (
                  <div key={i} className="px-3 py-2">
                    <div className="text-xs font-mono font-semibold text-sky-800">{tbl.table}</div>
                    <div className="mt-1 space-y-0.5">
                      {(Array.isArray(tbl.columns) ? tbl.columns : []).map((col: any, ci: number) => (
                        <div key={ci} className="flex items-baseline gap-2 text-[11px] font-mono">
                          <span className="text-gray-800">{col.name}</span>
                          <span className="text-gray-400">{col.type}</span>
                          {col.note && <span className="text-[9px] text-amber-600">{col.note}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Query editors */}
            <div className="lg:col-span-2 space-y-4">
              {queryTasks.map((qt, i) => (
                <div key={i} className="rounded-lg overflow-hidden border border-gray-300 shadow bg-white">
                  <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 text-xs font-medium text-gray-800 flex gap-2">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-sky-700 text-white text-[11px] flex items-center justify-center">
                      {i + 1}
                    </span>
                    {qt}
                  </div>
                  <textarea
                    value={queries[i] ?? ''}
                    onChange={(e) =>
                      setQueries((prev) => prev.map((q, idx) => (idx === i ? e.target.value : q)))
                    }
                    spellCheck={false}
                    rows={4}
                    className="w-full bg-[#1e1e1e] text-sky-200 font-mono text-xs p-3 outline-none resize-y"
                    placeholder={t('workspace.sqlPlaceholder') as string}
                  />
                </div>
              ))}

              {/* Sample data */}
              {sampleData.map((sd, i) => (
                <div key={i} className="rounded-lg overflow-hidden border border-gray-200 bg-white">
                  <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-gray-500 bg-gray-50">
                    {t('workspace.sampleData')} — <span className="font-mono">{sd.table}</span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-[11px]">
                      <thead>
                        <tr className="bg-gray-50">
                          {(Array.isArray(sd.headers) ? sd.headers : []).map((h: string, hi: number) => (
                            <th key={hi} className="px-2 py-1 text-left font-mono font-semibold text-gray-600">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {(Array.isArray(sd.rows) ? sd.rows : []).map((row: any[], ri: number) => (
                          <tr key={ri}>
                            {(Array.isArray(row) ? row : []).map((cell, ci) => (
                              <td key={ci} className="px-2 py-1 font-mono text-gray-700">{String(cell)}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          </div>
          {extras}
        </div>
      </div>
      <SubmitBar
        onSubmit={handleSubmit}
        submitting={submitting}
        disabled={queries.every((q) => !q.trim())}
        hint={t('workspace.sqlHint') as string}
      />
    </div>
  );
};

// ============================================================================
// 14) Network Diagram (topology troubleshooting)
// ============================================================================

const nodeTypeIcons: Record<string, string> = {
  router: '🌐',
  switch: '🔀',
  firewall: '🛡️',
  server: '🖥️',
  pc: '💻',
  ap: '📶',
};

const NetworkWorkspace: React.FC<WorkspaceSimulationProps> = ({ task, submitting, onSubmit, extras }) => {
  const { t } = useTranslation();
  const topology = task.topology || {};
  const nodes: any[] = Array.isArray(topology.nodes) ? topology.nodes : [];
  const links: any[] = Array.isArray(topology.links) ? topology.links : [];
  const questions: string[] = Array.isArray(task.questions) ? task.questions : [];
  const [answers, setAnswers] = useState<string[]>(questions.map(() => ''));

  const handleSubmit = () => {
    onSubmit({ answers: questions.map((q, i) => ({ question: q, answer: answers[i] || '' })) });
  };

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="flex-1 overflow-y-auto p-4">
        <div className="max-w-6xl mx-auto space-y-4">
          <TaskHeader
            icon={<Network size={20} className="text-violet-700" />}
            title={task.task_title}
            description={task.task_description}
            accent="bg-violet-50 border-violet-200"
          />

          {/* Problem report */}
          {task.problem_report && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex gap-2 text-sm text-red-800">
              <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
              {task.problem_report}
            </div>
          )}

          {/* Topology frame */}
          <div className="rounded-lg overflow-hidden border border-gray-300 shadow bg-white">
            <div className="flex items-center gap-2 px-3 py-2 bg-gray-100 text-xs border-b border-gray-200">
              <Network size={13} className="text-violet-600" />
              <span className="font-medium text-gray-800">{t('workspace.topology')}</span>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-gray-200">
              {/* Nodes */}
              <div>
                <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-gray-500 bg-gray-50">
                  {t('workspace.devices')}
                </div>
                <div className="divide-y divide-gray-100">
                  {nodes.map((node, i) => (
                    <div key={i} className="px-3 py-2 flex items-start gap-2 text-xs">
                      <span className="text-base leading-none">{nodeTypeIcons[node.type] || '📦'}</span>
                      <div className="min-w-0">
                        <div className="flex items-baseline gap-2">
                          <span className="font-mono font-semibold text-gray-900">{node.id}</span>
                          <span className="text-gray-600">{node.name}</span>
                        </div>
                        <div className="font-mono text-[11px] text-violet-700">{node.ip}</div>
                        {node.note && <div className="text-[10px] text-gray-500">{node.note}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Links */}
              <div>
                <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-gray-500 bg-gray-50">
                  {t('workspace.links')}
                </div>
                <ul className="px-3 py-2 space-y-1.5 text-xs font-mono text-gray-700">
                  {links.map((link, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <span className="font-semibold">{link.from}</span>
                      <span className="text-gray-400">──</span>
                      <span className="font-semibold">{link.to}</span>
                      {link.label && <span className="text-[10px] text-violet-600">({link.label})</span>}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Questions */}
          <div className="card space-y-3">
            <h4 className="text-sm font-semibold text-gray-900">
              {t('workspace.networkQuestions')}
            </h4>
            <QuestionAnswers
              questions={questions}
              answers={answers}
              onChange={(i, v) => setAnswers((prev) => prev.map((a, idx) => (idx === i ? v : a)))}
            />
          </div>
          {extras}
        </div>
      </div>
      <SubmitBar
        onSubmit={handleSubmit}
        submitting={submitting}
        disabled={answers.every((a) => !a.trim())}
        hint={t('workspace.networkHint') as string}
      />
    </div>
  );
};

// ============================================================================
// 15) Inventory Dashboard (stock control)
// ============================================================================

const InventoryWorkspace: React.FC<WorkspaceSimulationProps> = ({ task, submitting, onSubmit, extras }) => {
  const { t } = useTranslation();
  const inventory: any[] = Array.isArray(task.inventory) ? task.inventory : [];
  const openOrders: any[] = Array.isArray(task.open_orders) ? task.open_orders : [];
  const questions: string[] = Array.isArray(task.questions) ? task.questions : [];
  const [reorder, setReorder] = useState<Record<string, string>>({});
  const [answers, setAnswers] = useState<string[]>(questions.map(() => ''));

  const toggleReorder = (sku: string) => {
    setReorder((prev) => {
      const next = { ...prev };
      if (sku in next) delete next[sku];
      else next[sku] = '';
      return next;
    });
  };

  const handleSubmit = () => {
    onSubmit({
      reorder_plan: Object.entries(reorder).map(([sku, qty]) => ({ sku, qty: qty || '0' })),
      answers: questions.map((q, i) => ({ question: q, answer: answers[i] || '' })),
    });
  };

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="flex-1 overflow-y-auto p-4">
        <div className="max-w-6xl mx-auto space-y-4">
          <TaskHeader
            icon={<Package size={20} className="text-teal-700" />}
            title={task.task_title}
            description={task.task_description}
            accent="bg-teal-50 border-teal-200"
          />

          {/* Inventory table */}
          <div className="rounded-lg overflow-hidden border border-gray-300 shadow bg-white">
            <div className="flex items-center gap-2 px-3 py-2 bg-gray-100 text-xs border-b border-gray-200">
              <Package size={13} className="text-teal-600" />
              <span className="font-medium text-gray-800">{t('workspace.stockList')}</span>
              <span className="ml-auto text-[10px] text-gray-500">
                {t('workspace.reorderHint2')}
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-50 text-left text-[10px] uppercase tracking-wider text-gray-500">
                    <th className="px-3 py-2">SKU</th>
                    <th className="px-3 py-2">{t('workspace.product')}</th>
                    <th className="px-3 py-2 text-right">{t('workspace.stock')}</th>
                    <th className="px-3 py-2 text-right">{t('workspace.minStock')}</th>
                    <th className="px-3 py-2 text-right">{t('workspace.dailyUsage')}</th>
                    <th className="px-3 py-2 text-right">{t('workspace.leadTime')}</th>
                    <th className="px-3 py-2 text-right">{t('workspace.onOrder')}</th>
                    <th className="px-3 py-2">{t('workspace.supplier')}</th>
                    <th className="px-3 py-2 text-center">{t('workspace.reorderCol')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {inventory.map((item, i) => {
                    const selected = item.sku in reorder;
                    const low = Number(item.stock) <= Number(item.min_stock);
                    return (
                      <tr key={i} className={selected ? 'bg-teal-50/60' : low ? 'bg-red-50/40' : ''}>
                        <td className="px-3 py-2 font-mono font-semibold text-gray-900">{item.sku}</td>
                        <td className="px-3 py-2 text-gray-700">{item.name}</td>
                        <td className={`px-3 py-2 text-right font-mono font-semibold ${low ? 'text-red-600' : 'text-gray-900'}`}>
                          {item.stock}
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-gray-500">{item.min_stock}</td>
                        <td className="px-3 py-2 text-right font-mono text-gray-700">{item.daily_usage}</td>
                        <td className="px-3 py-2 text-right font-mono text-gray-700">{item.lead_time_days}</td>
                        <td className="px-3 py-2 text-right font-mono text-gray-700">{item.on_order}</td>
                        <td className="px-3 py-2 text-gray-500">{item.supplier}</td>
                        <td className="px-3 py-2">
                          <div className="flex items-center justify-center gap-1.5">
                            <input
                              type="checkbox"
                              checked={selected}
                              onChange={() => toggleReorder(item.sku)}
                              className="rounded border-gray-300"
                            />
                            {selected && (
                              <input
                                value={reorder[item.sku] ?? ''}
                                onChange={(e) =>
                                  setReorder((prev) => ({ ...prev, [item.sku]: e.target.value }))
                                }
                                placeholder={t('workspace.qty') as string}
                                className="input text-xs w-16 py-0.5 font-mono"
                              />
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Open orders */}
          {openOrders.length > 0 && (
            <div className="rounded-lg overflow-hidden border border-gray-200 bg-white">
              <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-gray-500 bg-gray-50">
                {t('workspace.openOrders')}
              </div>
              <div className="divide-y divide-gray-100">
                {openOrders.map((po, i) => (
                  <div key={i} className="px-3 py-2 flex items-center gap-3 text-xs">
                    <span className="font-mono font-semibold text-gray-900">{po.po}</span>
                    <span className="font-mono text-gray-600">{po.sku}</span>
                    <span className="text-gray-700">{po.qty} {t('workspace.pcs')}</span>
                    <span className="ml-auto text-teal-700 font-medium">{t('workspace.eta')}: {po.eta}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Questions */}
          <div className="card space-y-3">
            <h4 className="text-sm font-semibold text-gray-900">
              {t('workspace.inventoryQuestions')}
            </h4>
            <QuestionAnswers
              questions={questions}
              answers={answers}
              onChange={(i, v) => setAnswers((prev) => prev.map((a, idx) => (idx === i ? v : a)))}
            />
          </div>
          {extras}
        </div>
      </div>
      <SubmitBar
        onSubmit={handleSubmit}
        submitting={submitting}
        disabled={Object.keys(reorder).length === 0 && answers.every((a) => !a.trim())}
        hint={t('workspace.inventoryHint') as string}
      />
    </div>
  );
};

// ============================================================================
// Root component
// ============================================================================

const WorkspaceSimulation: React.FC<WorkspaceSimulationProps> = (rawProps) => {
  const { t } = useTranslation();
  const type = rawProps.task?.workspace_type;
  const { extras, collect } = useWorkspaceInteractions(rawProps.task);

  // Merge visual sub-task results (drag-drop, wiring, toggles...) into the submission
  const props: WorkspaceSimulationProps = {
    ...rawProps,
    extras,
    onSubmit: (submission) => {
      const interactionResults = collect();
      rawProps.onSubmit(
        interactionResults && Object.keys(interactionResults).length > 0
          ? { ...submission, interaction_results: interactionResults }
          : submission
      );
    },
  };

  switch (type) {
    case 'software_ide':
      return <IdeWorkspace {...props} />;
    case 'spreadsheet':
      return <SpreadsheetWorkspace {...props} />;
    case 'email_inbox':
      return <InboxWorkspace {...props} />;
    case 'data_notebook':
      return <NotebookWorkspace {...props} />;
    case 'scada_panel':
      return <ScadaWorkspace {...props} />;
    case 'design_review':
      return <DesignReviewWorkspace {...props} />;
    case 'crm_pipeline':
      return <CrmWorkspace {...props} />;
    case 'legal_review':
      return <LegalWorkspace {...props} />;
    case 'gcode_console':
      return <GcodeWorkspace {...props} />;
    case 'embedded_workbench':
      return <EmbeddedWorkspace {...props} />;
    case 'circuit_designer':
      return <CircuitWorkspace {...props} />;
    case 'plc_ladder':
      return <PlcLadderWorkspace {...props} />;
    case 'sql_console':
      return <SqlConsoleWorkspace {...props} />;
    case 'network_diagram':
      return <NetworkWorkspace {...props} />;
    case 'inventory_dashboard':
      return <InventoryWorkspace {...props} />;
    default:
      return (
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="card max-w-md text-center text-sm text-gray-600">
            {t('workspace.unsupportedType')}
          </div>
        </div>
      );
  }
};

export default WorkspaceSimulation;
